import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';
import { getPointOfSales, updatePointOfSale } from '../../../services/pointOfSalesService';
import { getProjects } from '../../../services/projectsService';

const VentasPosProjectConfig = () => {
  const { productsProjectEnabled } = useModuleSettings();
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const showToast = useCallback((message, type = 'info') => {
    setToast({ isOpen: true, message, type });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, projectsRes] = await Promise.all([
        getPointOfSales(),
        getProjects({ active_only: true }),
      ]);
      if (posRes.success) {
        setPointsOfSale(posRes.data || []);
      }
      if (projectsRes.success) {
        setProjects(projectsRes.data || []);
      }
    } catch {
      showToast('Error al cargar la configuración', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPoints = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return pointsOfSale;
    return pointsOfSale.filter(
      (pos) =>
        (pos.name || '').toLowerCase().includes(term) ||
        (pos.code || '').toLowerCase().includes(term) ||
        (pos.location || '').toLowerCase().includes(term),
    );
  }, [pointsOfSale, searchTerm]);

  const projectLabel = (projectId) => {
    if (!projectId) return '— Sin proyecto —';
    const project = projects.find((p) => String(p.id) === String(projectId));
    if (!project) return `Proyecto #${projectId}`;
    return project.code ? `${project.code} — ${project.name}` : project.name;
  };

  const handleProjectChange = async (pos, projectId) => {
    setSavingId(pos.id);
    try {
      const res = await updatePointOfSale(pos.id, {
        name: pos.name,
        location: pos.location,
        is_active: pos.is_active,
        default_project_id: projectId || null,
      });
      if (res.success) {
        setPointsOfSale((prev) =>
          prev.map((row) =>
            row.id === pos.id
              ? {
                  ...row,
                  default_project_id: res.data?.default_project_id ?? (projectId || null),
                  default_project: res.data?.default_project ?? row.default_project,
                }
              : row,
          ),
        );
        showToast('Proyecto del punto de venta actualizado', 'success');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar', 'error');
    } finally {
      setSavingId(null);
    }
  };

  if (!productsProjectEnabled) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800">Punto de venta a proyecto</h1>
        <p className="mt-3 text-gray-600">
          Para usar esta configuración, active primero la opción de proyecto en productos desde{' '}
          <Link to="/admin/general-settings" className="text-teal-700 hover:underline">
            Configuración general
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Punto de venta a proyecto</h1>
          <p className="text-gray-600 mt-2 max-w-3xl">
            Asigne un proyecto por defecto a cada punto de venta. Al crear cotizaciones o ventas, los
            productos nuevos tomarán ese proyecto automáticamente.
          </p>
        </div>

        <div className="mb-4">
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar punto de venta..."
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Punto de venta
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Ubicación
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Proyecto por defecto
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : filteredPoints.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    No hay puntos de venta para mostrar.
                  </td>
                </tr>
              ) : (
                filteredPoints.map((pos) => (
                  <tr key={pos.id} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{pos.name}</div>
                      <div className="text-xs text-gray-500">{pos.code}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{pos.location || '—'}</td>
                    <td className="px-4 py-3">
                      <MutateGate>
                        <select
                          value={pos.default_project_id ? String(pos.default_project_id) : ''}
                          onChange={(e) => handleProjectChange(pos, e.target.value || null)}
                          disabled={savingId === pos.id}
                          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-60"
                          title={projectLabel(pos.default_project_id)}
                        >
                          <option value="">— Sin proyecto —</option>
                          {projects.map((project) => (
                            <option key={project.id} value={String(project.id)}>
                              {project.code ? `${project.code} — ${project.name}` : project.name}
                            </option>
                          ))}
                        </select>
                      </MutateGate>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default VentasPosProjectConfig;
